import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { TopBar } from "@/components/fly/Logo";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignupsTab } from "@/components/fly/SignupsTab";
import { CategoriesTab } from "@/components/fly/CategoriesTab";
import { NewEditionTab } from "@/components/fly/NewEditionTab";
import { PlanTab, SendToOrganizer } from "@/components/fly/PlanTab";
import { DeleteAllSignups, MediaTab } from "@/components/fly/SignupTools";

const input = "h-10 px-3 rounded-lg bg-[#0B132B] border border-blue-500/20 focus:border-blue-500 outline-none text-sm";
const trig = "h-10 px-5 data-[state=active]:bg-blue-600 data-[state=active]:text-white";

const EventInfo = ({ settings, onSaved }) => {
  const [place, setPlace] = useState(settings.event_place || "Drezdenko");
  const [dates, setDates] = useState(settings.event_dates || "19–22.11.2026");
  const save = async () => {
    try {
      await api.put("/admin/settings", { event_place: place.trim(), event_dates: dates.trim() });
      toast.success("Zapisano datę i miejsce zawodów");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="fly-card p-5" data-testid="event-info">
      <h2 className="font-display text-xl font-bold uppercase">Data i miejsce zawodów</h2>
      <p className="text-xs text-slate-400 mt-1">Widoczne na górze strony głównej i w formularzu zapisów.</p>
      <div className="flex flex-wrap gap-3 mt-3 items-end">
        <label className="text-xs text-slate-400 grid gap-1">Miejsce<input data-testid="event-place-input" value={place} onChange={(e) => setPlace(e.target.value)} className={`${input} w-56`} /></label>
        <label className="text-xs text-slate-400 grid gap-1">Data<input data-testid="event-dates-input" value={dates} onChange={(e) => setDates(e.target.value)} placeholder="np. 19–22.11.2026" className={`${input} w-56`} /></label>
        <button data-testid="event-info-save" onClick={save} className="h-10 px-4 rounded-lg bg-blue-600 hover:bg-blue-500 text-sm font-semibold">Zapisz</button>
      </div>
    </section>
  );
};

export default function SignupsAdmin() {
  const [ov, setOv] = useState(null);
  const [rk, setRk] = useState(0);
  const load = useCallback(() => { api.get("/admin/overview").then((r) => setOv(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  const changed = () => { load(); setRk((x) => x + 1); };
  return (
    <>
      <TopBar title="Zapisy" back="/organizacja" />
      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-6 grid gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase">Zapisy</h1>
          <div className="flex flex-wrap gap-2">
            <SendToOrganizer onDone={changed} />
            <DeleteAllSignups onDone={changed} />
          </div>
        </div>
        {ov && <EventInfo settings={ov.settings} onSaved={load} />}
        <Tabs defaultValue="signups">
          <TabsList className="bg-[#1C2541] h-auto sm:h-12 p-1 w-full grid grid-cols-2 sm:flex gap-1">
            <TabsTrigger data-testid="sa-tab-signups" value="signups" className={trig}>Zgłoszenia{ov?.signups_pending ? ` (${ov.signups_pending})` : ""}</TabsTrigger>
            <TabsTrigger data-testid="sa-tab-plan" value="plan" className={trig}>Plan zawodów</TabsTrigger>
            <TabsTrigger data-testid="sa-tab-categories" value="categories" className={trig}>Kategorie</TabsTrigger>
            <TabsTrigger data-testid="sa-tab-media" value="media" className={trig}>Multimedia</TabsTrigger>
            <TabsTrigger data-testid="sa-tab-new" value="new" className="h-10 px-5 sm:ml-auto font-bold uppercase tracking-wide text-red-400 border border-red-500/40 data-[state=active]:bg-red-600 data-[state=active]:text-white">Nowy</TabsTrigger>
          </TabsList>
          <TabsContent value="signups"><SignupsTab key={`s${rk}`} settings={ov?.settings} onChange={changed} /></TabsContent>
          <TabsContent value="plan"><PlanTab key={`p${rk}`} onExported={changed} /></TabsContent>
          <TabsContent value="categories"><CategoriesTab /></TabsContent>
          <TabsContent value="media"><MediaTab /></TabsContent>
          <TabsContent value="new"><NewEditionTab onChange={changed} /></TabsContent>
        </Tabs>
      </main>
    </>
  );
}
